<template>
  <div :class="textAnnotationClass">
    <annotation-tool-bar
      v-show="isShowHighlightToolbar"
      ref="annotationToolBar"
      :button-config-list="getToolbarConfig(type)"
      :show-action="true"
      :annotatable="canAnnotate || canAssess"
      :can-ask-salis="canAskSalis"
      :is-ask-salis-supported-component="isAskSalisSupportedComponent"
      @color-and-shape-selected="onColorAndShapeSelected"
      @action-selected="onActionSelected"
    />
    <div class="dictionary-container">
      <dictionary-popup
        ref="dictionaryPopup"
        :visible="isDictionaryPopupOpened"
        :context="'highlight'"
        :popup-data="popupData"
        @popup-closed="onPopupClose"
        @add-to-note="onAddToNote"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { shouldUseInlineAnnotationEditor } from "@modules/annotation/service/annotation.editor.utils";
import { useDirty } from "@plugins/common/composables/page-dirty.ts";
import DOMUtils from "@utils/dom.utils";
import { isEmpty, isNull } from "@utils/var.check";
import {
  ANNOTATION_ACTION,
  ANNOTATION_CONTEXT_TYPE,
  ANNOTATION_MODE,
  ANNOTATION_TYPE,
  HIGHLIGHTER_SELECTOR_TYPE,
} from "../service/annotation.constant";
import AnnotationToolBar from "./AnnotationToolBar.vue";
import DictionaryPopup from "@plugins/dictionary/components/DictionaryPopup.vue";
import AnnotationValidationMixin from "@modules/annotation/mixin/annotation.validation.mixin";
import { usePageDrawer } from "@modules/app/composables/page.drawer.ts";
import { useAnnotation } from "../composables/annotation.ts";
import { useAnnotationAnchor } from "@modules/annotation/composables/annotation.anchor.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import AnnotationUtils from "@modules/annotation/service/annotation.utils";
import { DICTIONARY_OPTIONS } from "@modules/app-management/service/app.constant";
import { ANNOTATION_RICHTEXT_EDITOR_OPTIONS } from "@plugins/richtext/richtext.constant.ts";
import { useAnnotationNote } from "@modules/annotation/composables/annotation.note.ts";
import {
  computed,
  getCurrentInstance,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useTemplateRef,
  watch,
} from "vue";
import { useStore } from "vuex";
import { AnnotationContext } from "@modules/annotation/types.ts";
import { useVm } from "@utils/vm.ts";
import { useDynamicMountChild } from "@plugins/common/composables/dynamic-mount-child.ts";
import { CONVERSATION_ACTION } from "@/modules/conversation/services/conversation.constant.ts";
import { ConversationDrawerEventBus } from "@modules/conversation/services/conversation.drawer.event.bus.ts";

defineOptions({
  mixins: [AnnotationValidationMixin],
});

interface Props {
  parentComponent: object;
  containerElement?: HTMLElement;
  anchor?: string;
  annotationResourceType?: string;
  annotationResourceId?: string;
  canAnnotate?: boolean;
  canAssess?: boolean;
  canAskSalisFn?: () => boolean;
  isAskSalisSupportedComponent?: boolean;
  ownerType?: string;
  question?: unknown[];
  getMentionableTeacherList?: () => Record<string, unknown>;
  getMentionableStudentList?: () => Record<string, unknown>;
  // eslint-disable-next-line vue/prop-name-casing
  dynamicMountChildMixin_parentComponent?: object;
  // eslint-disable-next-line vue/prop-name-casing
  dynamicMountChildMixin_isDynamicallyMounted?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  containerElement: undefined,
  anchor: "",
  annotationResourceType: "",
  annotationResourceId: "",
  canAnnotate: false,
  canAssess: false,
  canAskSalisFn: () => false,
  isAskSalisSupportedComponent: false,
  ownerType: "USER",
  question: () => [],
  getMentionableTeacherList: () => ({}),
  getMentionableStudentList: () => ({}),
  dynamicMountChildMixin_parentComponent: () => ({}),
  dynamicMountChildMixin_isDynamicallyMounted: false,
});

useDynamicMountChild({
  dynamicMountChildMixin_parentComponent: props.dynamicMountChildMixin_parentComponent,
  dynamicMountChildMixin_isDynamicallyMounted: props.dynamicMountChildMixin_isDynamicallyMounted,
});

const annotationContext = inject<AnnotationContext>("annotationContext");

const store = useStore();
const { vm } = useVm();

const { handleCollapseDrawer } = usePageDrawer({
  drawerName: annotationContext?.drawerName ?? "",
});

const {
  annotations,
  getDefaultColor,
  getSelectedHighlight,
  getBaseHighlighterClass,
  getSelectedAnnotation,
  createDraftAnnotation,
  createAnnotation,
  getWithNote,
  setWithNote,
  setDictionaryPopupState,
  setSelectedHighlight,
  isShowHighlightToolbar,
  isAnnotationPopupOpened,
  isDictionaryPopupOpened,
  showHighlightToolbar,
  hideHighlightToolbar,
  getToolbarConfig,
} = useAnnotation(props.anchor);

const { handleAnchor } = useAnnotationAnchor();

const { promptPageDirty } = useDirty(() => props.anchor);

const { passiveApiFailHandler } = useErrorHandler();

const {
  selectedAnnotation,
  scrollToLastChangedAnnotation,
  populateAnnotationNoteContent,
  addTermToNoteContent,
  populateActivityNoteData,
  handleEditNotes,
  populateTextAnnotationData,
} = useAnnotationNote({
  deactiveNoteToggle: undefined,
  handleNoteChangeConfirm: undefined,
  handleNoteChangeCancel: undefined,
  getContext: undefined,
  getMentionableTeacherList: props.getMentionableTeacherList,
  getMentionableStudentList: props.getMentionableStudentList,
  mode: "",
});

const annotationToolBarRef =
  useTemplateRef<InstanceType<typeof AnnotationToolBar>>("annotationToolBar");
const dictionaryPopup = useTemplateRef<InstanceType<typeof DictionaryPopup>>("dictionaryPopup");

const mode = ref(ANNOTATION_MODE.VIEW);
const highlighter = ref(null);
const selectedText = ref(null);
const selectedTextContainer = ref(null);
const isImageRendererInteraction = ref(false);
const highlightData = ref({});
const highlightedContent = ref<string | null>(null);
const highlightedId = ref<string | null>(null);
const selectedHighlight = ref(null);
const popupData = ref({});
const initialOptionType = ref("");
const unwatchSelectedAnnotation = ref<(() => void) | null>(null);
const unwatchDictionaryPopupState = ref<(() => void) | null>(null);

const instance = getCurrentInstance();

const isView = computed(() => mode.value === ANNOTATION_MODE.VIEW);

const isEdit = computed(() => mode.value === ANNOTATION_MODE.EDIT);

const hasSelectedTextAnnotation = computed(() => {
  return (
    selectedAnnotation.value.annotation &&
    selectedAnnotation.value.annotation.type === ANNOTATION_TYPE.TEXT_ANNOTATION &&
    selectedAnnotation.value.annotation.anchor === props.anchor
  );
});

const type = computed(() => ANNOTATION_TYPE.TEXT_ANNOTATION);

const textAnnotationClass = computed(() => "text-annotation " + props.anchor);

const canAskSalis = computed(() => {
  if (typeof props.canAskSalisFn === "function") {
    return props.canAskSalisFn();
  }
  return false;
});

const isEditorContext = computed(
  () => annotationContext?.contextType === ANNOTATION_CONTEXT_TYPE.EDITOR,
);

const handleRichTextPointerDown = (event: PointerEvent): void => {
  const target = event.target;
  if (target instanceof Element && target.closest(".media-image-view")) {
    isImageRendererInteraction.value = true;
    hideHighlightToolbar();
    window.getSelection()?.removeAllRanges();
  }
};

watch(isShowHighlightToolbar, () => {
  const toolbar = DOMUtils.elOf(vm.$el, ".annotation-toolbar")[0];
  if (toolbar && toolbar instanceof HTMLElement) {
    toolbar.style.display = isShowHighlightToolbar.value ? "" : "none";
  }
});

watch(mode, () => {
  if (isView.value) {
    // Render updated annotations
    highlighter.value.removeAllHighlights();
    renderAnnotation();
  } else if (isEdit.value) {
    // Remove highlights during edit
    highlighter.value.removeAllHighlights();
    if (hasSelectedTextAnnotation.value) {
      // Only load the selected annotation that is not outdated
      if (!selectedAnnotation.value.annotation.outdated) {
        loadAnnotation(selectedAnnotation.value.annotation);
      }
      if (getWithNote.value) {
        nextTick(() => {
          const openEditor = () => {
            handleEditNotes(ANNOTATION_TYPE.TEXT_ANNOTATION, props.question, {
              initialOptionType: initialOptionType.value,
            });
            initialOptionType.value = "";
          };
          if (
            shouldUseInlineAnnotationEditor(
              selectedAnnotation.value.annotation,
              initialOptionType.value,
            )
          ) {
            openEditor();
          } else {
            handleCollapseDrawer().then(openEditor);
          }
        });
      }
    }
  }
});

watch(
  annotations,
  () => {
    if (!highlighter.value || !isView.value) {
      return;
    }

    highlighter.value.removeAllHighlights();
    renderAnnotation();
  },
  { deep: true },
);

onMounted(() => {
  selectedHighlight.value = getDefaultColor();
  setSelectedHighlight(getDefaultColor());
  setWatchStoreData();
  highlighter.value = instance?.appContext.config.globalProperties.$rangy.createHighlighter();
  renderAnnotation();
  bindLoadedAnnotation();
  props.containerElement?.addEventListener("pointerdown", handleRichTextPointerDown, true);
  checkSelectedAnnotation();
});

onBeforeUnmount(() => {
  props.containerElement?.removeEventListener("pointerdown", handleRichTextPointerDown, true);
  props.containerElement?.removeEventListener("click", handleClickAnnotation);

  if (unwatchSelectedAnnotation.value) unwatchSelectedAnnotation.value();
  if (unwatchDictionaryPopupState.value) unwatchDictionaryPopupState.value();
});

const setWatchStoreData = () => {
  unwatchSelectedAnnotation.value = store.watch(
    () => getSelectedAnnotation.value,
    () => {
      if (selectedAnnotation.value !== undefined) {
        if (selectedAnnotation.value.mode === ANNOTATION_MODE.EDIT) {
          mode.value = ANNOTATION_MODE.EDIT;
        } else if (selectedAnnotation.value.mode === ANNOTATION_MODE.DELETE) {
          mode.value = ANNOTATION_MODE.DELETE;
        } else if (selectedAnnotation.value.mode === ANNOTATION_MODE.VIEW) {
          mode.value = ANNOTATION_MODE.VIEW;
        }
      }
    },
  );
  unwatchDictionaryPopupState.value = store.watch(
    (state) => state.annotation.dictionaryPopupState,
    (value) => {
      if (value) {
        highlighter.value.removeAllHighlightsExcept(highlightedId.value);
      } else {
        highlighter.value.removeAllHighlights();
        renderAnnotation();
      }
    },
  );
};

const handleSelectionChange = (isPreviousComponent: boolean) => {
  const selection = instance?.appContext.config.globalProperties.$rangy.getSelection();
  if (isImageRendererInteraction.value) {
    if (!selection?.anchorNode || isEmpty(selection.toString())) {
      hideHighlightToolbar();
      isImageRendererInteraction.value = false;
      return;
    }
    isImageRendererInteraction.value = false;
  }

  if (!props.canAnnotate && !props.canAssess && !canAskSalis.value) {
    return;
  }
  // Do not show highlight toolbar when any popup is opened
  if (isDictionaryPopupOpened.value || isAnnotationPopupOpened.value) return;
  // This block of code should not be executed if canAnnotate = false and canAssess = false
  // Refer to AnnotatableRichText
  const container = props.containerElement;
  if (!container || !selection?.anchorNode) {
    selectedText.value = null;
    hideHighlightToolbar();
    return;
  }

  if (container.contains(selection.anchorNode) && !isEmpty(selection.toString())) {
    if (vm.validate()) {
      selectedText.value = selection;
      selectedTextContainer.value = saveSelection();
      if (isView.value) {
        showHighlightToolbar(isPreviousComponent);
        annotationToolBarRef.value.handleAnnotationSelected(selectedTextContainer.value);
      }
    } else {
      hideHighlightToolbar();
    }
  } else {
    selectedText.value = null;
    hideHighlightToolbar();
  }
};

const saveSelection = () => {
  if (window.getSelection) {
    const sel = window.getSelection();
    if (sel?.getRangeAt && sel?.rangeCount) {
      return sel?.getRangeAt(0).cloneRange();
    }
  }
  return null;
};

const restoreSelection = (range: Range | null) => {
  if (range) {
    if (window.getSelection) {
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
      if ((range as unknown as { select?: () => void }).select) {
        (range as unknown as { select: () => void }).select();
      }
    }
  }
};

const handleClickAnnotation = (e: Event) => {
  if (
    e.target &&
    (DOMUtils.hasClass(e.target, "highlight") || DOMUtils.hasClass(e.target, "assess"))
  ) {
    const id = (e.target as HTMLElement).getAttribute("data-id");
    const selection = instance?.appContext.config.globalProperties.$rangy.getSelection();
    if (isEditorContext.value || selection.isCollapsed || selection.toString() === "") {
      handleAnchor(id);
      // Keep the click from the editor's component-select handler, which would fire a
      // second, conflicting scroll.
      e.stopPropagation();
    }

    const audioPath = (e.target as HTMLElement).getAttribute("audio-path");
    if (!isEmpty(audioPath)) {
      handlePlayAudio(audioPath);
      e.stopPropagation();
    }
  }
};

const handlePlayAudio = (audioPath: string) => {
  const audioPlayer = document.createElement("audio");

  audioPlayer.src = audioPath;
  audioPlayer.play();

  audioPlayer.addEventListener("ended", function () {
    // JavaScript's garbage collector will automatically free up the memory occupied by the element when there are no more references to it
    if (audioPlayer) {
      audioPlayer.src = "";
    }
  });
};

const bindLoadedAnnotation = () => {
  const container = props.containerElement;
  if (!container) return;
  container.removeEventListener("click", handleClickAnnotation);
  container.addEventListener("click", handleClickAnnotation);
};

const onColorAndShapeSelected = () => {
  if (selectedTextContainer.value && selectedTextContainer.value.toString()) {
    restoreSelection(selectedTextContainer.value);
  }
};

const onActionSelected = async (action: string): Promise<void> => {
  if (!(await promptPageDirty())) return;
  switch (action) {
    case ANNOTATION_ACTION.HIGHLIGHT: {
      onHighlightOnlySelected();
      break;
    }
    case ANNOTATION_ACTION.HIGHLIGHT_WITH_NOTE: {
      onHighlightWithNotesSelected();
      break;
    }
    case ANNOTATION_ACTION.DEFINE: {
      onDefineSelected();
      break;
    }
    case ANNOTATION_ACTION.RECORD: {
      onHighlightWithRecordingSelected();
      break;
    }
    case ANNOTATION_ACTION.DRAW: {
      onHighlightWithDrawingSelected();
      break;
    }
    case CONVERSATION_ACTION.ASK_SALIS: {
      onAskSalis();
      break;
    }
  }
};

const onHighlightWithNotesSelected = () => {
  setWithNote(true);
  createTextAnnotation(getSelectedHighlight.value, true);
  hideHighlightToolbar();
};

const onHighlightWithRecordingSelected = () => {
  initialOptionType.value = ANNOTATION_RICHTEXT_EDITOR_OPTIONS.RECORDING;
  setWithNote(true);
  createTextAnnotation(getSelectedHighlight.value, true);
  hideHighlightToolbar();
};

const onHighlightWithDrawingSelected = () => {
  initialOptionType.value = ANNOTATION_RICHTEXT_EDITOR_OPTIONS.DRAWING;
  setWithNote(true);
  createTextAnnotation(getSelectedHighlight.value, true);
  hideHighlightToolbar();
};

const onHighlightOnlySelected = () => {
  setWithNote(false);
  createTextAnnotation(getSelectedHighlight.value, false);
  hideHighlightToolbar();
};

const onDefineSelected = () => {
  const id = Date.now();
  highlightedId.value = id;
  const div = DOMUtils.createElement("div", {});
  div.innerHTML = AnnotationUtils.getSelectionContent(selectedText.value);
  highlightedContent.value = div.textContent?.replaceAll("\n", "").trim() ?? "";
  addHighlighterClassApplier(id, getSelectedHighlight.value, true);
  const newHighlight = highlighter.value.highlightSelection(getSelectedHighlight.value, {
    containerElementId: props.anchor,
    selection: selectedText.value,
    exclusive: false,
  })[0];
  const selection = window.getSelection();
  if (selection) {
    selection.removeAllRanges();
  }
  highlightData.value = {
    highlighterId: id,
    highlighterClass: getSelectedHighlight.value,
    highlightedContent: highlightedContent.value,
    highlighterSelectors: [
      {
        type: HIGHLIGHTER_SELECTOR_TYPE.TEXT_POSITION_SELECTOR,
        start: newHighlight.characterRange.start,
        end: newHighlight.characterRange.end,
      },
    ],
    highlighter: highlighter.value,
    anchor: props.anchor,
    parentElement: getAnnotationContainer().commonAncestorContainer.parentElement,
  };
  hideHighlightToolbar();
  handleCollapseDrawer();
  const dictionarySettings = annotationContext.dictionarySettings;
  const dictionaryLanguage =
    Object.values(DICTIONARY_OPTIONS).find((op) => op.value === dictionarySettings)?.language ??
    DICTIONARY_OPTIONS.NONE.language;
  popupData.value = {
    highlightData: highlightData.value,
    dictionaryLanguage,
    dictionarySettings,
    canAddToNote: annotationContext.isNormalAttemptType && !annotationContext.isStudentStudentView,
  };
  setDictionaryPopupState(true);
};

const onAskSalis = () => {
  if (!selectedText.value || selectedText.value.rangeCount === 0) {
    return;
  }

  const container = document.getElementById(props.anchor);
  if (!container) {
    return;
  }

  const range = selectedText.value.getRangeAt(0);

  // Ensure selection is inside container
  if (!container.contains(range.commonAncestorContainer)) {
    return;
  }

  // Calculate start and end using rangy's character counting (consistent with selectCharacters)
  const rangy = instance?.appContext.config.globalProperties.$rangy;
  const rangyRange = rangy.createRange();
  rangyRange.setStart(range.startContainer, range.startOffset);
  rangyRange.setEnd(range.endContainer, range.endOffset);
  const charRange = rangyRange.toCharacterRange(container);
  const startIndex = charRange.start;
  const endIndex = charRange.end;

  const content = selectedText.value.toString();

  selectedText.value.removeAllRanges();
  hideHighlightToolbar();

  ConversationDrawerEventBus.$emit("chatContextProvided", {
    anchor: props.anchor,
    content: content,
    startIndex: startIndex,
    endIndex: endIndex,
  });
};

const onPopupClose = () => {
  setDictionaryPopupState(false);
  popupData.value = {};
};

const onAddToNote = (data: Record<string, unknown>) => {
  if (data.context === "search") return;
  const tempHighlighted = DOMUtils.el(".highlight-temporary");
  if (tempHighlighted?.length > 1) {
    tempHighlighted.forEach((temp) => {
      DOMUtils.addClass(temp, "highlight");
      DOMUtils.removeClass(temp, "highlight-temporary");
    });
  } else {
    DOMUtils.addClass(tempHighlighted, "highlight");
    DOMUtils.removeClass(tempHighlighted, "highlight-temporary");
  }
  selectedText.value.removeAllRanges();
  setWithNote(false);
  let annotationData = "";
  const noteContent = populateAnnotationNoteContent(data.content).outerHTML;
  if (highlightedContent.value === data.term) {
    // if content remains unchanged
    annotationData = populateTextAnnotationData(
      noteContent,
      highlightData.value,
      props.anchor,
      props.ownerType,
      vm.annotationResourceId,
    );
  } else {
    const noteContentWithTerm = addTermToNoteContent(noteContent, data.term);
    annotationData = populateActivityNoteData(noteContentWithTerm, "");
  }
  createAnnotation(JSON.stringify(annotationData)).then(() => {
    setDictionaryPopupState(false);
    dictionaryPopup.value.handleNotesSavedSuccessfully();
    if (data.annotationType === "TEXT_ANNOTATION") {
      handleAnchor(data.highlightedId);
    } else {
      scrollToLastChangedAnnotation(annotationContext?.drawerContentType ?? "");
    }
    popupData.value = {};
  }, onAddToNoteFail);
};

const onAddToNoteFail = () => {
  setDictionaryPopupState(false);
  passiveApiFailHandler();
};

const renderAnnotation = () => {
  annotations.value.forEach((annotation) => {
    loadAnnotation(annotation);
  });
};

const loadAnnotation = (annotation: Record<string, unknown>) => {
  const highlighterType = (annotation.data as Record<string, unknown>)?.highlighterClass;
  if (!isNull(highlighterType)) {
    const elementAttributes: Record<string, string> = {
      "data-id": "" + (annotation.data as Record<string, unknown>).highlighterId,
      "data-annotation-uuid": annotation.uuid as string,
      "data-color": (annotation.data as Record<string, unknown>).highlighterClass as string,
    };

    if (!isEmpty(annotation.audioFileUrl)) {
      elementAttributes["audio-path"] = annotation.audioFileUrl as string;
    }

    const highlighterClass = instance?.appContext.config.globalProperties.$rangy.createClassApplier(
      `annotation-${(annotation.data as Record<string, unknown>).highlighterId}`,
      {
        ignoreWhiteSpace: true,
        elementTagName: "sls-highlight",
        useExistingElements: false,
        elementAttributes: elementAttributes,
        elementProperties: {
          className: `${getBaseHighlighterClass((annotation.data as Record<string, unknown>).highlighterClass)} ${highlighterType}`,
        },
      },
    );
    highlighter.value.loadFromAnnotationData(props.anchor, annotation, highlighterClass);
  }
};

const createTextAnnotation = async (selectedHighlight: string, withNote = false): Promise<void> => {
  const id = Date.now();
  const content = AnnotationUtils.getSelectionContent(selectedText.value);
  addHighlighterClassApplier(id, selectedHighlight, false);
  const newHighlight = highlighter.value.highlightSelection(selectedHighlight, {
    containerElementId: props.anchor,
    selection: selectedText.value,
    exclusive: false,
  })[0];

  selectedText.value.removeAllRanges();

  const highlightData = populateHighlightData(
    id,
    selectedHighlight,
    content,
    newHighlight.characterRange.start,
    newHighlight.characterRange.end,
  );
  const requestData = populateTextAnnotationData(
    "",
    highlightData,
    props.anchor,
    props.ownerType,
    vm.annotationResourceId,
  );
  if (withNote) {
    await store.dispatch("annotation/setSelectedAnnotation", {
      annotation: null,
      mode: ANNOTATION_MODE.VIEW,
    });
    await nextTick();
    await createDraftAnnotation(requestData);
  } else {
    createAnnotation(JSON.stringify(requestData)).then(() => {
      selectedHighlight = getDefaultColor();
      handleAnchor(id);
    }, passiveApiFailHandler);
  }
};

const addHighlighterClassApplier = (id: number, color: string, isTemporary: boolean) => {
  const className = !isTemporary ? "highlight" : "highlight-temporary";
  const highlighterClass = instance?.appContext.config.globalProperties.$rangy.createClassApplier(
    color,
    {
      ignoreWhiteSpace: true,
      elementTagName: "sls-highlight",
      useExistingElements: false,
      elementAttributes: {
        "data-id": "" + id,
        "data-color": color,
      },
      elementProperties: {
        className: className,
      },
    },
  );

  highlighter.value.addClassApplier(highlighterClass);
};

const populateHighlightData = (
  id: number,
  color: string,
  content: string,
  start: number,
  end: number,
) => {
  return {
    highlighterId: id,
    highlighterClass: color,
    highlightedContent: content,
    highlighterSelectors: [
      {
        type: HIGHLIGHTER_SELECTOR_TYPE.TEXT_POSITION_SELECTOR,
        start: start,
        end: end,
      },
    ],
  };
};

const getAnnotationContainer = () => {
  return selectedTextContainer.value;
};

const getContext = () => {
  return new Promise((resolve, reject) => {
    if (selectedAnnotation.value && selectedAnnotation.value.annotation) {
      const selectedAnnotationValue = selectedAnnotation.value.annotation;
      resolve({
        resourceType: "ANNOTATION",
        resourceUuid: selectedAnnotationValue.uuid,
        resourceId: selectedAnnotationValue.id,
      });
      return;
    }

    // this case is not expected,
    // text annotation should have been created.
    reject();
  });
};

const checkSelectedAnnotation = () => {
  if (selectedAnnotation.value && selectedAnnotation.value.annotation) {
    if (selectedAnnotation.value !== undefined) {
      if (selectedAnnotation.value.mode === ANNOTATION_MODE.EDIT) {
        mode.value = ANNOTATION_MODE.EDIT;
      } else if (selectedAnnotation.value.mode === ANNOTATION_MODE.DELETE) {
        mode.value = ANNOTATION_MODE.DELETE;
      } else if (selectedAnnotation.value.mode === ANNOTATION_MODE.VIEW) {
        mode.value = ANNOTATION_MODE.VIEW;
      }
    }
  }
};

defineExpose({
  handleSelectionChange,
  onColorAndShapeSelected,
  onActionSelected,
  getContext,
});
</script>

<style lang="scss">
@import "@styles/abstracts/variables";
@import "@styles/layout/breakpoint";
@import "@styles/layout/z-map";
@import "@styles/themes/sls-theme/tokens/spacing";

.text-annotation {
  > .dictionary-container {
    position: fixed;
    width: 100vw;
    max-width: $main-content-width;
    z-index: z-map("content-popup");

    .content-popup-container {
      @include media("desktop") {
        max-width: calc(#{$main-content-width} - #{$layout-05} - #{$layout-05});
      }

      @include media("tablet") {
        max-width: calc(100vw - #{$layout-05} - #{$layout-05});
      }
    }
  }
}
</style>
